import { browserHistory } from 'react-router';

import { get, includes, isEmpty } from 'lodash';
import querystring from 'query-string';
import { call, CallEffect, put, select } from 'redux-saga/effects';

import { changeLocale, initApp, signOut } from '@lib/actions';
import { FETCH_CONTEXT, LOGIN_WIDGET_USER } from '@lib/actions/actionTypes';
import { normalizeLocale } from '@lib/actions/changeLocale';
import { sagaSave } from '@lib/actions/save';
import { patientFormsResource } from '@lib/resources';
import contextsResource from '@lib/resources/contextsResource';
import { RolloutEnum } from '@lib/resources/types';
import widgetUsersResource from '@lib/resources/widgetUsersResource';
import { sagaFetch, sagaValidateToken } from '@lib/sagas';
import { selectCurrentUser } from '@lib/selectors';
import storageService from '@lib/services/storageService';

import { registerSocket } from 'containers/App/actions';
import { getTokenData } from 'utils/getTokenData';
import request from 'utils/request';

import sagaFetchCurrentUser from './sagaFetchCurrentUser';
import sagaFetchCustomWebStyles from './sagaFetchCustomWebStyles';

// bind resource generator
const fetchContext = sagaFetch.bind(null, FETCH_CONTEXT, contextsResource.fetchContext);
const loginWidgetUser = sagaFetch.bind(null, LOGIN_WIDGET_USER, widgetUsersResource.login);

export default function* sagaInitApp(action: ReturnType<typeof initApp.request>): any {
  const {
    setParameters,
    get: storageGet,
    remove: storageRemove,
    setToken,
    getPatientFormPending,
    removePatientFormPending,
    getLocaleKeyByUserId,
  } = storageService;

  const {
    payload: { location, meta },
  } = action;
  // keep URL parameters on storage
  if (location && !isEmpty(location.query)) {
    const isReschedule = /reschedule/i.test(location.pathname);
    setParameters({
      reschedule: isReschedule,
      ...location.query,
    });
    // Clear localStorage if patient is rescheduling
    if (isReschedule && storageGet('patientFormId')) {
      storageRemove('patientFormId');
    }
  }

  // if user exists, login to get token

  // NOTE: some views are still using the token from the URL directly instead of getting it from the storage
  // which will cause problems in case we pass both: `user` and `token` URL parameters to those views
  // because they'll be using the token from the URL instead of the one from the user

  // FIXME: remove all appearances of location.query.token and use getToken when necessary
  const userId = get(location, 'query.user');
  const res = yield getTokenFromUserId(userId);

  if (res?.error) {
    yield put(signOut({}));
    return;
  }

  const token = action.payload.token || selectToken(res, userId);

  let roles = get(getTokenData(token), 'roles', []);

  if (!token) {
    yield put(signOut({}));
    return;
  }

  setToken(token);

  // skip fetching auth for some screens
  const pathname = location ? location.pathname : '';
  const skipAuth = /verify|telehealth/.test(pathname);

  if (!skipAuth) {
    let validatedToken: any = {};

    // validate token when is not a widget user
    if (!userId) {
      validatedToken = yield sagaValidateToken({ token });

      if (validatedToken.error) {
        yield put(signOut({}));
        return;
      }

      roles = get(validatedToken, 'data.roles', []);
    }

    // FIXME different patients might log into the app so we don't want to keep old IDs on the storage
    // First time the app loads all storage should be cleaned up/replaced
    if (includes(roles, 'widget')) {
      storageRemove('patientFormId');
    }

    if (!location || !/login/.test(location.pathname)) {
      // fetch current user
      yield sagaFetchCurrentUser();

      // fetch context
      // FIXME we want to fetch the context no matter if we're validating the token/fetching the user
      const refId = get(location, 'query.refId');
      if (refId) {
        yield call(fetchContext, { refId });
      }
    }
  }
  const currentUser = yield select(selectCurrentUser);

  let patientFormStarted;
  if (currentUser) {
    // Set patient on pending patient forms
    // TODO use the `payload.location` passed inside action
    const searchParamsString = window.location.search;
    const searchParams = querystring.parse(searchParamsString);

    // TODO diegopessigatt avoid fetching and updating the patientForm each time we open the patient form page
    // This workaround should only be for the patient form ID that is marked as pending inside the local storage
    // Related: https://github.com/lumahealthhq/patient-web-app/commit/d1da9aad769b510b8f347da930101f0c5d054fbe
    const queryPatientFormId = get(searchParams, 'patientForm', null);

    const pendingPatientFormId = getPatientFormPending() || queryPatientFormId;

    if (pendingPatientFormId && currentUser.type === 'patient' && !includes(roles, 'patientLoginOnly')) {
      const patientForm = yield select(patientFormsResource.makeSelectEntity(pendingPatientFormId));
      let patientFormResponse = {};
      if (!patientForm) {
        patientFormResponse = yield call(request, `/patientForms/${pendingPatientFormId}`, { method: 'get' });
      }

      try {
        // TODO resolve `save` action using `putResolve`
        // @ts-ignore
        patientFormStarted = yield call(sagaSave, {
          payload: {
            resource: patientFormsResource,
            data: {
              id: pendingPatientFormId,
              ...{ ...patientForm, ...patientFormResponse },
              patient: currentUser.id,
            },
            meta: { ignoreErrors: true, skipMessages: true },
          },
        });
      } catch (e) {
        removePatientFormPending();
      }

      const linkedFormsStorage = storageService.get('lh:linked-forms');
      const patientFormIdStorage = storageService.get('patientFormId');
      const hasLinkedForm = linkedFormsStorage && patientFormIdStorage && linkedFormsStorage[patientFormIdStorage];
      if (!hasLinkedForm) {
        removePatientFormPending();
      }
    }
    if (!currentUser.$isWidgetUser) yield put(registerSocket(currentUser));
  }

  const isLumabot = /lumabot/.test(pathname);

  if (token && !isLumabot) {
    // update locale
    const locale = storageGet(getLocaleKeyByUserId(get(currentUser, 'id', ''))) || get(currentUser, 'language');
    yield put(changeLocale(normalizeLocale(locale)));

    // fetch theme
    yield sagaFetchCustomWebStyles({ payload: { id: get(location, 'query.customWebStylesId', null) } });
  }

  // app ready
  yield put(initApp.success({}));

  // callbackSuccess
  if (meta && meta.callbackSuccess) {
    const patientForm = patientFormStarted?.data;
    meta.callbackSuccess({ ...(res || {}), ...(patientForm && { patientForm }) });
  }

  // Go to the returnUrl once the app is ready
  if (currentUser?.rollouts?.includes(RolloutEnum.NextyPatientLogin)) {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('returnUrl');
    if (returnUrl) {
      const newUrl = new URL(returnUrl, window.location.origin);
      newUrl.searchParams.delete('token');
      browserHistory.push(newUrl.href);
    }
  }
}

function* getTokenFromUserId(
  userId?: string,
): Generator<CallEffect, { data?: unknown; error?: unknown } | undefined, { data?: unknown; error?: unknown }> {
  if (!userId) return;
  return yield call(loginWidgetUser, { id: userId });
}

function selectToken(res?: unknown, userId?: string) {
  const tokenFromUserId = get(res, 'data.token', '');
  if (userId && tokenFromUserId) return tokenFromUserId;

  const tokenFromStorage = storageService.getToken();

  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromQuery = urlParams.get('token');

  return tokenFromQuery || tokenFromStorage;
}
